import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Row, Col, Statistic, Table, Tag, Button, Modal, Form, Input,
  message, Typography, Tabs, Space, Tooltip
} from 'antd';
import {
  TeamOutlined, DollarOutlined, AuditOutlined, CheckCircleOutlined,
  CloseCircleOutlined, SendOutlined
} from '@ant-design/icons';
import referralService, {
  AdminReferralOverview, AdminReferralItem, AdminCommissionItem, AdminWithdrawalItem
} from '../services/referral';

const { Title, Text } = Typography;

const formatCents = (cents: number) => `$${(cents / 100).toFixed(2)}`;

const statusColors: Record<string, string> = {
  pending: 'orange', settled: 'green', withdrawn: 'blue', cancelled: 'red',
  approved: 'cyan', rejected: 'red', completed: 'green', active: 'green', disabled: 'default',
};
const statusLabels: Record<string, string> = {
  pending: '待处理', settled: '已结算', withdrawn: '已提现', cancelled: '已取消',
  approved: '已通过', rejected: '已驳回', completed: '已完成', active: '有效', disabled: '已禁用',
};

const ReferralAdmin: React.FC = () => {
  const [overview, setOverview] = useState<AdminReferralOverview | null>(null);
  const [referrals, setReferrals] = useState<AdminReferralItem[]>([]);
  const [referralsTotal, setReferralsTotal] = useState(0);
  const [commissions, setCommissions] = useState<AdminCommissionItem[]>([]);
  const [commissionsTotal, setCommissionsTotal] = useState(0);
  const [withdrawals, setWithdrawals] = useState<AdminWithdrawalItem[]>([]);
  const [withdrawalsTotal, setWithdrawalsTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [completeModalVisible, setCompleteModalVisible] = useState(false);
  const [currentWithdrawalId, setCurrentWithdrawalId] = useState<number>(0);
  const [rejectForm] = Form.useForm();
  const [completeForm] = Form.useForm();

  const loadOverview = useCallback(async () => {
    try {
      const data = await referralService.getAdminOverview();
      setOverview(data);
    } catch { /* ignore */ }
  }, []);

  const loadReferrals = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const data = await referralService.getAdminReferrals(0, page, 10);
      setReferrals(data.list || []);
      setReferralsTotal(data.total);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  const loadCommissions = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const data = await referralService.getAdminCommissions(0, '', page, 10);
      setCommissions(data.list || []);
      setCommissionsTotal(data.total);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  const loadWithdrawals = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const data = await referralService.getAdminWithdrawals('', page, 10);
      setWithdrawals(data.list || []);
      setWithdrawalsTotal(data.total);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    loadOverview();
    loadReferrals();
    loadCommissions();
    loadWithdrawals();
  }, [loadOverview, loadReferrals, loadCommissions, loadWithdrawals]);

  const handleApprove = async (id: number) => {
    Modal.confirm({
      title: '确认通过',
      content: '确认通过此提现申请？',
      onOk: async () => {
        try {
          await referralService.reviewWithdrawal(id, 'approve');
          message.success('已通过');
          loadWithdrawals();
          loadOverview();
        } catch (err: any) {
          message.error(err?.response?.data?.message || '操作失败');
        }
      },
    });
  };

  const handleReject = (id: number) => {
    setCurrentWithdrawalId(id);
    setRejectModalVisible(true);
  };

  const handleRejectSubmit = async () => {
    const values = await rejectForm.validateFields();
    try {
      await referralService.reviewWithdrawal(currentWithdrawalId, 'reject', values.reason);
      message.success('已驳回');
      setRejectModalVisible(false);
      rejectForm.resetFields();
      loadWithdrawals();
      loadOverview();
    } catch (err: any) {
      message.error(err?.response?.data?.message || '操作失败');
    }
  };

  const handleComplete = (id: number) => {
    setCurrentWithdrawalId(id);
    setCompleteModalVisible(true);
  };

  const handleCompleteSubmit = async () => {
    const values = await completeForm.validateFields();
    try {
      await referralService.completeWithdrawal(currentWithdrawalId, values.tx_hash);
      message.success('已标记完成');
      setCompleteModalVisible(false);
      completeForm.resetFields();
      loadWithdrawals();
      loadOverview();
    } catch (err: any) {
      message.error(err?.response?.data?.message || '操作失败');
    }
  };

  const referralColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: '推荐人', dataIndex: 'referrer_username', key: 'referrer' },
    { title: '推荐人ID', dataIndex: 'referrer_client_id', key: 'referrer_id', width: 80 },
    { title: '被邀请人', dataIndex: 'referee_username', key: 'referee' },
    { title: '被邀请人ID', dataIndex: 'referee_client_id', key: 'referee_id', width: 90 },
    { title: '邀请码', dataIndex: 'invite_code', key: 'code' },
    { title: '状态', dataIndex: 'status', key: 'status', render: (s: string) => <Tag color={statusColors[s]}>{statusLabels[s] || s}</Tag> },
    { title: '时间', dataIndex: 'created_at', key: 'time', render: (t: string) => new Date(t).toLocaleString() },
  ];

  const commissionColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: '推荐人', dataIndex: 'referrer_username', key: 'referrer' },
    { title: '被邀请人', dataIndex: 'referee_username', key: 'referee' },
    { title: '订单ID', dataIndex: 'order_id', key: 'order', ellipsis: true },
    { title: '订单金额', dataIndex: 'order_amount_cents', key: 'amount', render: (v: number) => formatCents(v) },
    { title: '比例', dataIndex: 'commission_rate', key: 'rate', render: (v: number) => `${v}%` },
    { title: '佣金', dataIndex: 'commission_amount_cents', key: 'com', render: (v: number) => <Text strong style={{ color: '#52c41a' }}>{formatCents(v)}</Text> },
    { title: '状态', dataIndex: 'status', key: 'status', render: (s: string) => <Tag color={statusColors[s]}>{statusLabels[s] || s}</Tag> },
    { title: '时间', dataIndex: 'created_at', key: 'time', render: (t: string) => new Date(t).toLocaleString() },
  ];

  const withdrawalColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: '用户', dataIndex: 'username', key: 'user' },
    { title: '金额', dataIndex: 'amount_cents', key: 'amount', render: (v: number) => formatCents(v) },
    { title: '收款地址', dataIndex: 'wallet_address', key: 'addr', ellipsis: true, render: (v: string) => <Tooltip title={v}>{v.slice(0, 8)}...{v.slice(-6)}</Tooltip> },
    { title: '状态', dataIndex: 'status', key: 'status', render: (s: string) => <Tag color={statusColors[s]}>{statusLabels[s] || s}</Tag> },
    { title: 'TX Hash', dataIndex: 'tx_hash', key: 'tx', ellipsis: true, render: (v: string) => v || '-' },
    { title: '时间', dataIndex: 'created_at', key: 'time', render: (t: string) => new Date(t).toLocaleString() },
    {
      title: '操作', key: 'action', width: 200,
      render: (_: any, record: AdminWithdrawalItem) => (
        <Space>
          {record.status === 'pending' && (
            <>
              <Button type="link" size="small" icon={<CheckCircleOutlined />} onClick={() => handleApprove(record.id)}>通过</Button>
              <Button type="link" size="small" danger icon={<CloseCircleOutlined />} onClick={() => handleReject(record.id)}>驳回</Button>
            </>
          )}
          {record.status === 'approved' && (
            <Button type="link" size="small" icon={<SendOutlined />} onClick={() => handleComplete(record.id)}>标记打款</Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Title level={3}><AuditOutlined /> 推广管理</Title>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <Card><Statistic title="总推荐关系" value={overview?.total_referrals || 0} prefix={<TeamOutlined />} /></Card>
        </Col>
        <Col span={8}>
          <Card><Statistic title="总佣金记录" value={overview?.total_commissions || 0} prefix={<DollarOutlined />} /></Card>
        </Col>
        <Col span={8}>
          <Card><Statistic title="待审核提现" value={overview?.pending_withdrawals || 0} prefix={<AuditOutlined />} valueStyle={{ color: overview?.pending_withdrawals ? '#faad14' : undefined }} /></Card>
        </Col>
      </Row>

      <Card>
        <Tabs defaultActiveKey="withdrawals" items={[
          {
            key: 'withdrawals',
            label: `提现审批 (${withdrawalsTotal})`,
            children: <Table columns={withdrawalColumns} dataSource={withdrawals} rowKey="id" loading={loading} pagination={{ total: withdrawalsTotal, pageSize: 10, onChange: loadWithdrawals }} size="small" />,
          },
          {
            key: 'referrals',
            label: `推荐关系 (${referralsTotal})`,
            children: <Table columns={referralColumns} dataSource={referrals} rowKey="id" loading={loading} pagination={{ total: referralsTotal, pageSize: 10, onChange: loadReferrals }} size="small" />,
          },
          {
            key: 'commissions',
            label: `佣金流水 (${commissionsTotal})`,
            children: <Table columns={commissionColumns} dataSource={commissions} rowKey="id" loading={loading} pagination={{ total: commissionsTotal, pageSize: 10, onChange: loadCommissions }} size="small" />,
          },
        ]} />
      </Card>

      <Modal title="驳回提现" open={rejectModalVisible} onCancel={() => setRejectModalVisible(false)} onOk={handleRejectSubmit} okText="确认驳回" okButtonProps={{ danger: true }}>
        <Form form={rejectForm} layout="vertical">
          <Form.Item name="reason" label="驳回原因" rules={[{ required: true, message: '请输入驳回原因' }]}>
            <Input.TextArea rows={3} placeholder="请输入驳回原因" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="标记打款完成" open={completeModalVisible} onCancel={() => setCompleteModalVisible(false)} onOk={handleCompleteSubmit} okText="确认完成">
        <Form form={completeForm} layout="vertical">
          <Form.Item name="tx_hash" label="链上交易哈希 (TX Hash)" rules={[{ required: true, message: '请输入交易哈希' }]}>
            <Input placeholder="输入链上交易 hash" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ReferralAdmin;
